import { useGetList, type Identifier } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";

import { cn } from "@/lib/utils";
import { pickNextStep } from "../../deals/nextStep";
import { NAV_ROW_ATTR } from "../../layout/shortcuts";
import { addDaysKey, formatDateShort } from "../../misc/osloDate";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import type {
  Company,
  CompanyNextStep,
  ContactType,
  Deal,
  WorkItem,
} from "../../types";
import type { DriftScan } from "./buildRows";
import { ToneDot } from "./FocusRow";
import {
  formatValue,
  rowReason,
  rowTone,
  type FocusRow,
  type Scope,
} from "./ranking";

// «Kunder» (M3): én rad per kunde, sortert etter kundens høyeste poeng, med
// kolonnene Kunde, Hvorfor nå, Sist kontakt, Neste steg og Mulighet. Under
// tabellen én linje om kundene som er i rute (kontakt siste 30 dager, et
// neste steg frem i tid og ingen driftsfunn).

const KIND_LABEL: Record<ContactType, string> = {
  call: "Ring",
  meeting: "Møte",
  email: "E-post",
  other: "Kontakt",
};

type LastContact = {
  id: Identifier;
  company_id: Identifier;
  last_contact_at: string | null;
  last_contact_kind?: ContactType | null;
};

export type CustomerRow = {
  key: string;
  companyId: Identifier;
  name: string;
  top: FocusRow;
};

/** Én rad per kunde, i rangeringens rekkefølge (høyeste poeng først). */
export const customerRows = (ranked: FocusRow[]): CustomerRow[] => {
  const seen = new Set<string>();
  const out: CustomerRow[] = [];
  for (const row of ranked) {
    if (row.companyId == null) continue;
    const id = String(row.companyId);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({
      key: `cust:${id}`,
      companyId: row.companyId,
      name: row.companyName ?? row.title,
      top: row,
    });
  }
  return out;
};

const dealValue = (deal: Deal) => deal.annual_value ?? deal.amount ?? null;

export const CustomerTable = ({
  ranked,
  scope,
  me,
  today,
  companies,
  openDeals,
  tasks,
  steps,
  drift,
  selected,
  onSelect,
  onOpen,
  onOpenFull,
}: {
  ranked: FocusRow[];
  scope: Scope;
  me: Identifier | null;
  today: string;
  companies: Company[];
  openDeals: Deal[];
  tasks: WorkItem[];
  steps: CompanyNextStep[];
  drift: DriftScan[];
  selected: string | null;
  onSelect: (row: CustomerRow) => void;
  onOpen: (row: CustomerRow) => void;
  onOpenFull: (row: CustomerRow) => void;
}) => {
  const { dealStages } = useConfigurationContext();
  const [showOnTrack, setShowOnTrack] = useState(false);
  const { data: lastContacts = [] } = useGetList<LastContact>(
    "company_last_contact",
    {
      pagination: { page: 1, perPage: 5000 },
      sort: { field: "company_id", order: "ASC" },
    },
  );
  const lastBy = new Map(lastContacts.map((r) => [String(r.company_id), r]));
  const stepBy = new Map(steps.map((s) => [String(s.company_id), s]));
  const dealsBy = new Map<string, Deal[]>();
  for (const d of openDeals) {
    if (d.company_id == null) continue;
    const key = String(d.company_id);
    dealsBy.set(key, [...(dealsBy.get(key) ?? []), d]);
  }
  const tasksByDeal = new Map<string, WorkItem[]>();
  for (const t of tasks) {
    if (t.deal_id == null || t.completed_at) continue;
    const key = String(t.deal_id);
    tasksByDeal.set(key, [...(tasksByDeal.get(key) ?? []), t]);
  }
  const driftBy = new Set(
    drift.filter((d) => d.findings.length).map((d) => String(d.companyId)),
  );

  const nextStepOf = (companyId: string) => {
    const deals = dealsBy.get(companyId) ?? [];
    for (const deal of deals) {
      const next = pickNextStep(tasksByDeal.get(String(deal.id)));
      if (next) return { text: next.title, due: next.due_date ?? null };
    }
    const step = stepBy.get(companyId);
    if (step?.description) {
      return { text: step.description, due: step.due_date ?? null };
    }
    return deals.length ? { missing: true as const } : null;
  };

  const rows = customerRows(ranked);
  const inTable = new Set(rows.map((r) => String(r.companyId)));
  const recent = addDaysKey(today, -30);
  const onTrack = companies.filter((c) => {
    const id = String(c.id);
    if (c.archived_at || c.is_own_company || inTable.has(id)) return false;
    if (scope === "mine" && String(c.sales_id) !== String(me)) return false;
    if (scope === "unowned" && c.sales_id != null) return false;
    const last = lastBy.get(id)?.last_contact_at?.slice(0, 10);
    const next = nextStepOf(id);
    return (
      !!last &&
      last >= recent &&
      !!next &&
      !("missing" in next) &&
      !!next.due &&
      next.due >= today &&
      !driftBy.has(id)
    );
  });

  return (
    <section aria-labelledby="kunder-title" className="flex flex-col gap-3">
      <h2 id="kunder-title" className="text-[13px] font-semibold">
        Kunder
        <span className="ml-2 font-normal text-muted-foreground">
          {rows.length}, sortert etter rangeringen
        </span>
      </h2>
      <div
        role="table"
        aria-label="Kunder som trenger noe"
        className="overflow-hidden rounded-[10px] border bg-card text-[13px]"
      >
        <div
          role="row"
          className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.4fr)_minmax(0,0.9fr)_minmax(0,1.2fr)_minmax(0,1fr)] gap-4 border-b px-4 py-2 text-xs text-muted-foreground"
        >
          <span role="columnheader">Kunde</span>
          <span role="columnheader">Hvorfor nå</span>
          <span role="columnheader">Sist kontakt</span>
          <span role="columnheader">Neste steg</span>
          <span role="columnheader">Mulighet</span>
        </div>
        {rows.length === 0 ? (
          <p className="px-4 py-4 text-muted-foreground">
            Ingen kunder trenger noe nå.
          </p>
        ) : (
          rows.map((row) => {
            const id = String(row.companyId);
            const last = lastBy.get(id);
            const next = nextStepOf(id);
            const deal = [...(dealsBy.get(id) ?? [])].sort(
              (a, b) => (dealValue(b) ?? 0) - (dealValue(a) ?? 0),
            )[0];
            const stage = deal
              ? dealStages.find((s) => s.value === deal.stage)?.label
              : null;
            const value = deal ? dealValue(deal) : null;
            const isSelected = row.key === selected;
            return (
              <div
                key={row.key}
                role="row"
                {...{ [NAV_ROW_ATTR]: "" }}
                data-row-key={row.key}
                tabIndex={isSelected ? 0 : -1}
                aria-current={isSelected ? "true" : undefined}
                aria-label={`${row.name}. ${rowReason(row.top)}`}
                onClick={() => onOpen(row)}
                onFocus={() => onSelect(row)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return;
                  if (event.key === "Enter") {
                    event.preventDefault();
                    if (event.shiftKey) onOpenFull(row);
                    else onOpen(row);
                  }
                }}
                className={cn(
                  "relative grid min-h-12 cursor-default grid-cols-[minmax(0,1.2fr)_minmax(0,1.4fr)_minmax(0,0.9fr)_minmax(0,1.2fr)_minmax(0,1fr)] items-center gap-4 border-b px-4 py-2.5 outline-none last:border-b-0",
                  "hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-inset",
                  isSelected && "bg-muted/50",
                )}
              >
                {isSelected && (
                  <span
                    aria-hidden
                    className="absolute inset-y-0 left-0 w-0.5 bg-primary"
                  />
                )}
                <span role="cell" className="truncate font-medium">
                  {row.name}
                </span>
                <span role="cell" className="flex min-w-0 items-center gap-1.5">
                  <ToneDot tone={rowTone(row.top)} />
                  <span className="truncate">{rowReason(row.top)}</span>
                </span>
                <span
                  role="cell"
                  className="truncate text-muted-foreground tabular-nums"
                >
                  {last?.last_contact_at
                    ? `${last.last_contact_kind ? `${KIND_LABEL[last.last_contact_kind] ?? "Kontakt"}, ` : ""}${formatDateShort(last.last_contact_at)}`
                    : "Aldri"}
                </span>
                <span
                  role="cell"
                  className={cn(
                    "truncate",
                    next && "missing" in next
                      ? "text-red-500"
                      : "text-muted-foreground",
                  )}
                >
                  {next == null
                    ? "—"
                    : "missing" in next
                      ? "Mangler"
                      : `${next.text}${next.due ? `, ${formatDateShort(next.due)}` : ""}`}
                </span>
                <span
                  role="cell"
                  className="truncate text-muted-foreground tabular-nums"
                >
                  {deal
                    ? [stage, value ? formatValue(value) : null]
                        .filter(Boolean)
                        .join(" · ")
                    : "—"}
                </span>
              </div>
            );
          })
        )}
      </div>
      {onTrack.length > 0 && (
        <div className="flex flex-col gap-2 text-[13px] text-muted-foreground">
          <p>
            {onTrack.length === 1
              ? "1 annen aktiv kunde er i rute"
              : `${onTrack.length} andre aktive kunder er i rute`}{" "}
            (kontakt siste 30 dager, neste steg planlagt, ingen driftsfunn).{" "}
            <button
              type="button"
              onClick={() => setShowOnTrack((v) => !v)}
              className="underline underline-offset-4 hover:text-foreground"
            >
              {showOnTrack ? "Skjul" : "Vis"}
            </button>
          </p>
          {showOnTrack && (
            <ul className="flex flex-wrap gap-x-4 gap-y-1">
              {onTrack.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/companies/${c.id}/show`}
                    className="hover:text-foreground hover:underline"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};
